import { useEffect, useState } from "react";
import { ButtonGroup, Button, Modal, Form } from 'react-bootstrap';
import axios from 'axios';
import ControlledForm from "~/shared/form/ControlledForm";
import TextInput from "~/shared/form/TextInput";
import { useForm } from "react-hook-form";
import FormSection from "~/shared/form/FormSection";
import FormIconButton from "~/shared/form/FormIconButton";
import { formatPhoneNumber, getFullName, getSessionData, localeFormatDate, setSessionData, yearsSinceDate } from "~/shared/utils";
import Chip from "~/shared/Chip";
import { Link, useHistory } from 'react-router-dom';
import usePreMount from "~/shared/use-pre-mount";
import DataTable from "~/shared/DataTable";
import AutoSelectInput from "~/shared/form/AutoSelectInput";
import useAxiosData from "~/shared/use-axios-data";
import FormResponse from "~/shared/form/FormResponse";
import FormButton from "~/shared/form/FormButton";
import useModal from "~/shared/UseModal";

export default function ManageFamilies() {
    const [showAddFamilyModal, setShowAddFamilyModal] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searchResultsGroup, setSearchResultsGroup] = useState("family");
    const [group, setGroup] = useState("family");
    const [programs] = useAxiosData(() => axios.get("programs"), []);
    const formHook = useForm();
    const { setValue, formState: { isSubmitSuccessful } } = formHook;
    const history = useHistory();
    const { ModalDialog, showConfirm } = useModal();

    usePreMount(() => {
        const state = history.location.state ?? getSessionData("manage-families-state");

        setSessionData("manage-families-state", state);
        setGroup(state.group);
        setSearchResults(state.searchResults);
        setSearchResultsGroup(state.searchResultsGroup);
        setValue("query", state.query);
    }, history.location.state || getSessionData("manage-families-state"));

    useEffect(() => {
        if (!history.location.state && !getSessionData("manage-families-state")) {
            document.querySelector("#search-btn").click();
        }
    }, []);

    async function onSubmit({ query }) {
        if (query.match(/\d/g)?.join("").length === 10) {
            query = formatPhoneNumber(query);
        }

        const response = await axios.post(group === "family" ? "admin/search-families" : "admin/search-children", { query });
        setSearchResults(response.data);
        setSearchResultsGroup(group);

        if (response.data.length > 0) {
            const state = { query, group, searchResults: response.data, searchResultsGroup: group };
            updateCache(state);
        } else {
            updateCache(undefined);
        }
    }

    function updateCache(state) {
        history.replace(history.location.pathname, state);
        setSessionData("manage-families-state", state);
    }

    async function changeChildProgram(id, child, programName) {
        if (!programName) {
            programName = null;
        }

        const program = programName ? programs.find(program => program.name === programName) : null;
        await axios.post(`admin/child/${child._id}`, { id, program: program?._id ?? null });
        searchResults.find(f => f._id === id).children.find(c => c === child).program = program;
        updateCache({ ...getSessionData("manage-families-state"), group, searchResults, searchResultsGroup });
    }

    async function deleteFamily(id, context) {
        if (!await showConfirm("Delete Family", "Are you sure you want to permanently delete this family account?")) {
            return false;
        }

        context.setLoading(true);
        await axios.post("admin/family/delete", { id });
        setSearchResults(searchResults => searchResults.filter(r => r._id !== id));
    }

    async function deleteChild(id, child, context) {
        if (!await showConfirm("Delete Child", "Are you sure you want to permanently delete this child?")) {
            return false;
        }

        context.setLoading(true);
        await axios.post(`admin/child/delete/${child._id}`, { id });
        setSearchResults(searchResults => {
            const family = searchResults.find(r => r._id === id);
            family.children = family.children.filter(c => c._id !== child._id);
            return [...searchResults];
        });
    }

    function preNavigate(_id, { lastName }) {
        setSessionData(`family/${_id}`, { lastName });
    }

    function getFamilyActions({ _id, parent }) {
        return <>
            <DataTable.Action><Link onClick={() => preNavigate(_id, parent)} to={`/family/${_id}`}>View</Link></DataTable.Action>
            <DataTable.Action onClick={(_, context) => deleteFamily(_id, context)} manualSpinner>Delete</DataTable.Action>
        </>;
    }

    function getChildActions({ _id, parent, child }) {
        return <>
            <DataTable.Action><Link onClick={() => preNavigate(_id, parent)} to={`/family/${_id}/child/${child._id}`}>View</Link></DataTable.Action>
            <DataTable.Action><Link onClick={() => preNavigate(_id, parent)} to={`/family/${_id}/child/${child._id}?edit`}>Edit</Link></DataTable.Action>
            <DataTable.Action onClick={(_, context) => deleteChild(_id, child, context)} manualSpinner>Delete</DataTable.Action>
        </>;
    }

    console.log(searchResults?.[0]?.children.map(c => c.program));

    return (
        <section>
            {ModalDialog}
            <ButtonGroup className="w-full mb-2">
                <Button variant="light" onClick={() => setGroup("family")} active={group === "family"}>Families</Button>
                <Button variant="light" onClick={() => setGroup("child")} active={group === "child"}>Children</Button>
            </ButtonGroup>
            <ControlledForm hook={formHook} onSubmit={onSubmit} className="mt-1 mb-2">
                <FormSection row wrap gap={0.5} className="large">
                    <TextInput label="Query" id="query" className="large" placeholder={group === "family" ? "Search families..." : "Search children..."} noMargin hideLabel hideValidation />
                    <FormIconButton legacy submit icon="bi-search" id="search-btn" />
                    {group === "family" && <FormIconButton legacy onClick={() => setShowAddFamilyModal(true)} noDisable icon="bi-person-plus-fill" />}
                </FormSection>
                <Form.Text>{group === "family" ? "You can search families by the parent's name, email, or phone number." : "You can search children by their name."}</Form.Text>
            </ControlledForm>
            <div className="w-full">
                {group === searchResultsGroup && (searchResults.length > 0 || isSubmitSuccessful) && (
                    group === "family" ? (
                        <DataTable data={searchResults} paginate>
                            <DataTable.Column id="parent" format={getFullName} sortable sortBy={getFullName}>Parent Name</DataTable.Column>
                            <DataTable.Column id="email" sortable>Email</DataTable.Column>
                            <DataTable.Column id="parent.mainPhone" sortable>Phone</DataTable.Column>
                            <DataTable.Column showAt="md" value={({ _id, parent, children }) => children.length === 0 ? "None" : children.map(c => (
                                <Chip key={c._id} className="my-1" onClick={() => (preNavigate(_id, parent), history.push(`/family/${_id}/child/${c._id}`))}>
                                    {getFullName(c)}, age {yearsSinceDate(c.dateOfBirth)}
                                </Chip>
                            ))}>Children</DataTable.Column>
                            <DataTable.Column value={getFamilyActions}>Actions</DataTable.Column>
                            <DataTable.Message condition={searchResults?.length === 0}>
                                <i className="text-muted mt-2">No Results Found</i>
                            </DataTable.Message>
                        </DataTable>
                    ) : (
                        <DataTable data={searchResults.map(({ _id, parent, children }) => children.map(child => ({ _id, parent, child }))).flat()} paginate>
                            <DataTable.Column id="child" format={getFullName} sortable sortBy={getFullName}>Name</DataTable.Column>
                            <DataTable.Column id="child.dateOfBirth" showAt="sm" format={localeFormatDate} sortable>Date of Birth</DataTable.Column>
                            <DataTable.Column sortId="childDob" id="child.dateOfBirth" format={yearsSinceDate} sortable sortBy={({ child }) => child.dateOfBirth}>Age</DataTable.Column>
                            <DataTable.Column value={({ _id, child }) => (
                                <AutoSelectInput
                                    initial={child.program?.name} placeholder="None"
                                    update={p => changeChildProgram(_id, child, p)} options={programs.map(p => p.name)}
                                    confirm={p => ["Change Program", `Are you sure you want to change ${child.firstName} ${child.lastName}'s program to: ${p}?`]}
                                />
                            )}>Program</DataTable.Column>
                            <DataTable.Column value={getChildActions}>Actions</DataTable.Column>
                            <DataTable.Message condition={searchResults?.length === 0}>
                                <i className="text-muted mt-2">No Results Found</i>
                            </DataTable.Message>
                        </DataTable>
                    )
                )}
            </div>
            <Modal show={showAddFamilyModal} onHide={() => setShowAddFamilyModal(false)} className="fixed-size-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Add New Family</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ControlledForm post="auth/register-family" success="Successfully added family." resetOnSuccess>
                        <p>Enter an email and password below to create a new family account.</p>
                        <TextInput label="Email" id="email" preset="email" required />
                        <TextInput label="Password" id="password" validate={(password, { confirm }) => password === confirm} invalid="Passwords must match." deps="confirm" preset="password" required>
                            <Form.Text>Password must be at least 8 characters long.</Form.Text>
                        </TextInput>
                        <TextInput label="Confirm Password" id="confirm" validate={(confirm, { password }) => password === confirm} invalid="Passwords must match." deps="password" preset="password" required />
                        <FormResponse />
                        <FormButton legacy submit>Create Family</FormButton>
                    </ControlledForm>
                </Modal.Body>
            </Modal>
        </section>
    );
}
